import { useTranslation } from 'react-i18next';
import type { Project } from '@interfaces/IProject';
import { BuildingIcon, MapPinIcon } from '@utils/icons';
import { StatusBadges } from '../StatusBadges/StatusBadges';
import { ManagerCard } from '../ManagerCard/ManagerCard';
import { useProjectPhoto } from './useProjectPhoto';
import styles from './styles.module.css';

function mapsHref(location: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
}

type IPhotoPanelProps = {
  project: Project;
  isFavorite: boolean;
  onToggleFavorite: () => void;
};

/**
 * The card's left column: the project photo under a dark scrim, with the
 * favourite star and status badges on top and the name, location and manager
 * at the bottom. Without a photo (none, or still loading) it is a branded
 * gradient with a faint building — never a broken image. Its controls sit
 * inside the navigable card, so each keeps its click to itself.
 */
export function PhotoPanel({ project, isFavorite, onToggleFavorite }: IPhotoPanelProps) {
  const { t } = useTranslation();
  const photoUrl = useProjectPhoto(project.id);
  return (
    <div className={styles.panel} data-testid="project-photo-panel" data-has-photo={photoUrl ? 'true' : 'false'}>
      <span className={styles.fallbackIcon} aria-hidden>
        <BuildingIcon size={84} />
      </span>
      {photoUrl ? (
        <div className={styles.photo} data-testid="project-photo" style={{ backgroundImage: `url("${photoUrl}")` }} />
      ) : null}
      <div className={styles.scrim} aria-hidden />

      <div className={styles.top}>
        <button
          type="button"
          className={styles.star}
          aria-pressed={isFavorite}
          aria-label={t(isFavorite ? 'portfolio.card.removeFavorite' : 'portfolio.card.addFavorite')}
          data-favorite={isFavorite ? 'true' : undefined}
          onClick={(e) => {
            // The star sits inside the navigable card — keep its click local.
            e.stopPropagation();
            onToggleFavorite();
          }}
        >
          {isFavorite ? '★' : '☆'}
        </button>
        <StatusBadges lifecycle={project.status} schedule={project.scheduleStatus} onImage />
      </div>

      <div className={styles.bottom}>
        <div className={styles.identity}>
          <div className={styles.name}>{project.name}</div>
          <a
            className={styles.location}
            href={mapsHref(project.location)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            <MapPinIcon />
            {project.location}
          </a>
        </div>
        <div className={styles.manager}>
          <ManagerCard managers={project.managers} />
        </div>
      </div>
    </div>
  );
}
